import type { ParsedGeometry } from '../models/types';
import type { ParserRequest, ParserResponse } from './worker-messages';

export interface IfcLoadCallbacks {
  onGeometry: (geometry: ParsedGeometry) => void;
}

interface PendingJob {
  file: File;
  callbacks: IfcLoadCallbacks;
  resolve: (geometryCount: number) => void;
  reject: (error: Error) => void;
  signal: AbortSignal;
}

export class IfcLoaderService {
  private readonly queue: PendingJob[] = [];
  private activeJobs = 0;
  private readonly maxParallelJobs = 2;

  load(file: File, callbacks: IfcLoadCallbacks, signal: AbortSignal): Promise<number> {
    return new Promise((resolve, reject) => {
      this.queue.push({ file, callbacks, resolve, reject, signal });
      this.pumpQueue();
    });
  }

  private pumpQueue(): void {
    while (this.activeJobs < this.maxParallelJobs && this.queue.length > 0) {
      const job = this.queue.shift();
      if (!job) return;
      if (job.signal.aborted) {
        job.reject(new DOMException('Loading cancelled.', 'AbortError'));
        continue;
      }
      this.activeJobs += 1;
      void this.runJob(job).finally(() => {
        this.activeJobs -= 1;
        this.pumpQueue();
      });
    }
  }

  private async runJob(job: PendingJob): Promise<void> {
    const worker = new Worker(new URL('./ifc-parser.worker.ts', import.meta.url), {
      type: 'module',
    });
    const jobId = crypto.randomUUID();
    let rejectWorker: ((reason: unknown) => void) | undefined;
    const abortError = new DOMException('Loading cancelled.', 'AbortError');
    const abort = () => {
      worker.terminate();
      if (rejectWorker) rejectWorker(abortError);
      else job.reject(abortError);
    };
    job.signal.addEventListener('abort', abort, { once: true });

    try {
      const data = await job.file.arrayBuffer();
      if (job.signal.aborted) return;

      await new Promise<void>((resolve, reject) => {
        rejectWorker = reject;
        worker.onmessage = (event: MessageEvent<ParserResponse>) => {
          const message = event.data;
          if (message.jobId !== jobId) return;
          if (message.type === 'geometry') job.callbacks.onGeometry(message.geometry);
          if (message.type === 'complete') {
            job.resolve(message.geometryCount);
            resolve();
          }
          if (message.type === 'error') reject(new Error(message.message));
        };
        worker.onerror = (event) => reject(new Error(event.message || 'IFC worker failed.'));

        const request: ParserRequest = {
          type: 'parse',
          jobId,
          data,
          wasmPath: `${window.location.origin}${import.meta.env.BASE_URL}wasm/`,
        };
        worker.postMessage(request, [data]);
      });
    } catch (error) {
      if (!job.signal.aborted) {
        const normalized = error instanceof Error ? error : new Error('Unable to load IFC file.');
        job.reject(normalized);
      }
    } finally {
      rejectWorker = undefined;
      job.signal.removeEventListener('abort', abort);
      worker.terminate();
    }
  }
}
